import MarketIcon from 'src/components/Utils/MarketIcon';
import { cj } from 'src/utils/classNames';
import usePnlModalCardPreview from 'src/components/Modals/PnlModal/hooks/usePnlModalCardPreview';
import {
	PnlModalCardPreviewVariantProps,
	PnlModalVariant,
} from 'src/components/Modals/PnlModal/types';
import { PNL_MODAL_MAX_COLLATERAL_DISPLAY_COUNT } from 'src/components/Modals/PnlModal/consts';
import PnlModalCardPreviewVelocityLogos from 'src/components/Modals/PnlModal/PnlModalCardPreviewVelocityLogos';
import { APP_DOMAIN } from 'src/constants/site';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';

interface Props {
	cardPreviewVariantProps: PnlModalCardPreviewVariantProps;
	backgroundImagePathIndex: number;
	showCollateral: boolean;
	imageGenerationNodeRef: React.RefObject<HTMLDivElement | null>;
}

export default function PnlModalCardPreview({
	cardPreviewVariantProps,
	backgroundImagePathIndex,
	showCollateral,
	imageGenerationNodeRef,
}: Props) {
	const {
		isMobileScreenSize,
		marketSymbol,
		direction,
		leverageString,
		pnlDisplay,
		entryExit,
		balanceAssetSymbolsByQuoteValueDesc,
		referralLink,
		isPositivePnl,
		backgroundImagePaths,
		pnlDisplayFontSize,
		accountName,
		period,
	} = usePnlModalCardPreview(cardPreviewVariantProps);

	const { variant } = cardPreviewVariantProps;

	const content = (() => {
		if (variant === 'portfolio-performance') {
			return (
				<div className="flex flex-col gap-y-[14px] pt-7 sm:gap-y-lg sm:pt-8">
					<span className="text-[1.125rem] whitespace-nowrap sm:typo-heading-h5">
						{accountName}
					</span>

					<div className="flex flex-col gap-y-2xs">
						<Label variant="portfolio-performance">Trade Portfolio P&L</Label>

						<PnlDisplay
							isPositivePnl={isPositivePnl}
							pnlDisplayFontSize={pnlDisplayFontSize}
						>
							{pnlDisplay}
						</PnlDisplay>

						<Label variant="portfolio-performance">Period: {period}</Label>
					</div>
				</div>
			);
		}

		return (
			<>
				<div className="flex flex-1 flex-col justify-center gap-y-md sm:gap-y-xl">
					<div className="flex flex-col gap-y-2xs">
						{/* Market logo, symbol, position direction, and leverage. */}
						{marketSymbol && (
							<div className="flex flex-1 items-center gap-x-sm">
								<MarketIcon
									marketSymbol={marketSymbol}
									sizeClass="size-4"
									useNextApiRoute
								/>
								<span className="text-xs whitespace-nowrap not-sm:font-bold sm:typo-heading-h5">
									{marketSymbol}
								</span>
								<span
									className={cj(
										'text-xs whitespace-nowrap uppercase not-sm:font-bold sm:typo-heading-h5',
										direction === 'long'
											? 'text-text-positive-green-button'
											: 'text-text-negative-red-button'
									)}
								>
									{direction} {leverageString}
								</span>
							</div>
						)}

						{/* PnL */}
						<PnlDisplay
							isPositivePnl={isPositivePnl}
							pnlDisplayFontSize={pnlDisplayFontSize}
						>
							{pnlDisplay}
						</PnlDisplay>
					</div>

					<div className="flex flex-col gap-y-sm sm:gap-y-lg">
						{/* Entry and Mark / Exit */}
						{entryExit !== undefined && (
							<div className="flex flex-1 items-center gap-x-md sm:gap-x-xl">
								{entryExit.map(({ label, value }) => (
									<div key={label} className="flex items-center gap-x-xs">
										<Label variant="position">{label}</Label>
										<span className="text-3xs not-sm:font-medium sm:typo-label-md">
											${value}
										</span>
									</div>
								))}
							</div>
						)}

						{/* Collateralized with... */}
						{showCollateral &&
							balanceAssetSymbolsByQuoteValueDesc &&
							balanceAssetSymbolsByQuoteValueDesc.length > 0 && (
								<div className="flex-sm flex items-center gap-x-xs">
									<Label variant="position">Collateralized with</Label>
									{balanceAssetSymbolsByQuoteValueDesc
										.slice(0, PNL_MODAL_MAX_COLLATERAL_DISPLAY_COUNT)
										.map((symbol) => (
											<MarketIcon
												key={symbol}
												marketSymbol={symbol}
												sizeClass={isMobileScreenSize ? 'size-3' : 'size-4'}
												useNextApiRoute
											/>
										))}
									{balanceAssetSymbolsByQuoteValueDesc.length >
										PNL_MODAL_MAX_COLLATERAL_DISPLAY_COUNT && (
										<Label variant="position">
											+{' '}
											{balanceAssetSymbolsByQuoteValueDesc.length -
												PNL_MODAL_MAX_COLLATERAL_DISPLAY_COUNT}
										</Label>
									)}
								</div>
							)}
					</div>
				</div>

				{/* Referral link */}
				<Label variant="position" className="flex items-end">
					{referralLink ? referralLink : APP_DOMAIN}
				</Label>
			</>
		);
	})();

	return (
		<div
			ref={imageGenerationNodeRef}
			className={cj(
				'relative z-10 h-full w-full overflow-hidden bg-main-bg',
				'flex flex-col text-white',
				isMobileScreenSize ? 'p-xl' : 'p-8',
				'aspect-58/35'
			)}
		>
			{/* Background image */}
			<img
				className="absolute inset-0 -z-10 h-full w-full object-cover"
				src={backgroundImagePaths[backgroundImagePathIndex]}
				alt=""
			/>

			{/* Drift Logos */}
			<div className="relative h-[18px] w-[54px] sm:h-[26px] sm:w-[78px]">
				<PnlModalCardPreviewVelocityLogos />
			</div>

			{content}
		</div>
	);
}

interface LabelProps extends WithChildren, WithClassName {
	variant: PnlModalVariant;
}

function Label({ children, className, variant }: LabelProps) {
	const variantClassName = {
		'portfolio-performance': 'text-xs sm:typo-body-sm text-text-secondary',
		position: 'text-[8px] sm:typo-body-xs text-neutral-600',
	}[variant];

	return (
		<span
			className={cj(
				'whitespace-nowrap not-sm:font-medium',
				variantClassName,
				className
			)}
		>
			{children}
		</span>
	);
}

interface PnlDisplayProps extends WithChildren {
	isPositivePnl: boolean;
	pnlDisplayFontSize: string;
}

function PnlDisplay({
	children,
	isPositivePnl,
	pnlDisplayFontSize,
}: PnlDisplayProps) {
	return (
		<span
			className={cj(
				'flex-1 leading-none font-bold whitespace-nowrap sm:leading-tight',
				isPositivePnl
					? 'text-text-positive-green-button'
					: 'text-text-negative-red-button',
				pnlDisplayFontSize
			)}
		>
			{children}
		</span>
	);
}
